// https://github.com/apollographql/apollo-client-nextjs.
// This is the client-side version of the Apollo client.

"use client";
import { HttpLink } from "@apollo/client";
import { Defer20220824Handler } from "@apollo/client/incremental";
import { LocalState } from "@apollo/client/local-state";
import { relayStylePagination } from "@apollo/client/utilities";
import {
  ApolloClient,
  ApolloNextAppProvider,
  InMemoryCache,
} from "@apollo/client-integration-nextjs";

import possibleTypes from "@/__generated__/possibleTypes";
import { apiUrl } from "@/lib/api/client";

function makeClient() {
  const httpLink = new HttpLink({
    uri: apiUrl("graphql"),
    fetchOptions: {
      cache: "no-store",
      credentials: "include",
    },
  });

  return new ApolloClient({
    cache: new InMemoryCache({
      possibleTypes: possibleTypes.possibleTypes,
      typePolicies: {
        Admin: {
          keyFields: [],
          fields: {
            // AGENT: keyArgs exclude first/after so later pages append instead of replacing the list.
            mattersConnection: relayStylePagination([
              "search",
              "owners",
              "states",
              "types",
              "workspaces",
              "sortField",
              "sortDirection",
            ]),
          },
        },
        AdminSnapshottedForm: {
          keyFields: ["id", "formType"],
        },
        // AGENT: a source's id is its record id, so a page classification and a QBO snapshot can share one; the type tells them apart.
        AdminTaxOrganizerSource: {
          keyFields: ["id", "sourceType"],
        },
        // Income lines use label as id, which is not unique across scenarios.
        // Disable normalization so they're stored inline with their parent scenario.
        AdminTaxEstimateIncomeLine: {
          keyFields: false,
        },
      },
    }),

    link: httpLink,
    defaultOptions: {
      watchQuery: {
        errorPolicy: "all",
      },
      query: {
        errorPolicy: "all",
      },
      mutate: {
        errorPolicy: "all",
      },
    },

    /*
    Inserted by Apollo Client 3->4 migration codemod.
    If you are not using the `@client` directive in your application,
    you can safely remove this option.
    */
    localState: new LocalState({}),

    /*
    Inserted by Apollo Client 3->4 migration codemod.
    If you are not using the `@defer` directive in your application,
    you can safely remove this option.
    */
    incrementalHandler: new Defer20220824Handler(),
  });
}

export function ApolloWrapper({ children }: React.PropsWithChildren) {
  return (
    <ApolloNextAppProvider makeClient={makeClient}>
      {children}
    </ApolloNextAppProvider>
  );
}
